/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-shadow */
/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { XCircle, MagnifyingGlass } from 'phosphor-react';
import queryString from 'query-string';

import { downloadBibs, fetchBibsList, fetchBibsSearch } from '../../../services/apis/business/bibs';

import AllowedIDs from '../../../helper/restrictions/allowedIDs.json';

import InputField from '../../../components/fields/input';

import Spinner from '../../../components/loader/spinner';

import Pagination from '../../../components/layout/pagination';
import LoadingButton from '../../../components/button';

function BibsParticipantsList() {
  const { user } = useSelector(state => state.adminSession);

  const location = useLocation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [metadata, setMetadata] = useState(null);
  const [race, setRace] = useState(null);
  const [header, setHeader] = useState([]);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);

  const { control, setValue, watch } = useForm({
    defaultValues: {
      search: '',
    },
  });

  const search = watch('search');

  const bibsSearch = useCallback(async (raceID, eventName, searchTerm) => {
    try {
      const response = await fetchBibsSearch(raceID, eventName, searchTerm);

      const { race, header, records } = response;
      const { results: list, totalDocs, totalPages, hasPrevPage, hasNextPage } = records;

      setRace(race);
      setHeader(header);
      setResults(list);
      setMetadata({
        totalDocs,
        totalPages,
        hasPrevPage,
        hasNextPage,
      });
      setSearched(true);
      setFetching(false);
    } catch (error) {
      setFetching(false);
    }
  }, []);

  const bibsList = useCallback(async (raceID, event, page) => {
    try {
      const response = await fetchBibsList(raceID, event, page);

      const { race, header, records } = response;
      const { results: list, totalDocs, totalPages, hasPrevPage, hasNextPage } = records;

      setRace(race);
      setHeader(header);
      setResults(list);
      setMetadata({
        totalDocs,
        totalPages,
        hasPrevPage,
        hasNextPage,
      });
      setSearched(false);
      setFetching(false);
    } catch (error) {
      setFetching(false);
    }
  }, []);

  const handleSearchKeyDown = useCallback(
    code => {
      if (code === 13) {
        if (search) {
          bibsSearch(race.id, race.event, search);
        } else {
          bibsList(race.id, race.event, 1);
        }
      }
    },
    [search, race],
  );

  const handleSearchClick = useCallback(() => {
    if (search) {
      bibsSearch(race.id, race.event, search);
    } else {
      bibsList(race.id, race.event, 1);
    }
  }, [search, race]);

  const handleSearchCancel = useCallback(() => {
    setValue('search', '');
    bibsList(race.id, race.event, 1);
  }, [race]);

  const handleDownloadBibs = useCallback(async () => {
    setLoading(true);

    try {
      const response = await downloadBibs(race.id, race.event);

      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'BIBsCollection.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [race]);

  useEffect(() => {
    const { race: raceID = null, event = null } = queryString.parse(location.search);

    (async () => {
      setFetching(true);

      await bibsList(raceID, event, page);
    })();
  }, [page]);

  return (
    <>
      <Helmet>
        <title>{race?.name ? `Participants | ${race?.name}` : 'Loading...'}</title>
      </Helmet>
      <div className="min-height">
        <div className="banner">
          <img src={race?.logo} alt="Race Logo" />
          <p>{race?.name}</p>
          <p>{race?.date}</p>
        </div>
        <div className="container max-w-4xl mx-auto pt-6 px-6 md:px-8">
          <div className="flex flex-col-reverse sm:flex-row justify-evenly sm:justify-between">
            <div className="flex items-center justify-center sm:justify-start">
              {searched && (
                <button
                  type="button"
                  className="button button--text-only !px-0"
                  style={{ marginRight: '10px' }}
                  onClick={() => handleSearchCancel()}>
                  <XCircle size={20} weight="bold" />
                </button>
              )}
              <InputField
                name="search"
                control={control}
                placeholder="Search By Name, Mobile or BIB"
                onKeyDown={event => handleSearchKeyDown(event.keyCode)}
              />
              <button type="button" className="button ml-2" onClick={() => handleSearchClick()}>
                <MagnifyingGlass size={18} weight="bold" />
              </button>
            </div>
            {AllowedIDs.includes(user.id) && (
              <div className="flex flex-col md:flex-row items-center justify-end ml-4 mb-4 md:mb-0">
                <LoadingButton
                  label="Download"
                  loading={loading}
                  disabled={loading}
                  onClick={handleDownloadBibs}
                />
              </div>
            )}
          </div>
        </div>
        <div className="container max-w-5xl mx-auto pb-6 px-6">
          {fetching && (
            <div className="mt-8">
              <Spinner size="medium" />
            </div>
          )}
          {!fetching &&
            (results.length > 0 ? (
              <>
                <div className="table-responsive bg-white shadow-xl rounded-md p-3 mt-8 mb-6 border border-gray-300 no-scrollbar">
                  <table className="table table-hover">
                    <thead>
                      <tr className="border border-t-0 border-r-0 border-l-0 border-b-2">
                        {header.map((item, index) => (
                          <th key={index} scope="col">
                            <span className="text-xs text-black font-semibold">{item.name}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="text-xs">
                      {results.map((result, index) => (
                        <tr
                          key={index}
                          className="transition-colors duration-500"
                          onClick={() =>
                            navigate(
                              `/bibs/details?raceID=${race.id}&event=${race.event}&bibNo=${result.bibNo}&name=${result.name}`,
                            )
                          }>
                          {Object.keys(result).map((item, index) => (
                            <td key={index}>{result[item]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!searched && (
                  <Pagination
                    currentPage={Number(page)}
                    step={2}
                    metadata={metadata}
                    onChange={value => setPage(value)}
                  />
                )}
              </>
            ) : (
              <div className="table-responsive lg:px-20 py-8">
                <p className="mb-1 text-md">
                  <strong>No Participants Found</strong>
                </p>
              </div>
            ))}
        </div>
      </div>
    </>
  );
}

export default BibsParticipantsList;
